import {
  type JourneyConsentState,
  isStepThatAsks,
} from "@/components/AIAutomation/journey-consent-gate";
import { isPermissionFlowAvailable } from "@/helpers/electron-helper";
import { getOS, OPERATING_SYSTEM } from "@/helpers/os-helper";

/**
 * Whether the macOS grant walkthrough should be put to the new hire right now.
 *
 * Every rule lives here so a call site cannot forget one. These are macOS panes, so a Windows hire
 * must never see them. A hire who refused consent has nothing to grant for, and a build that cannot
 * probe could not tell them whether a grant landed.
 *
 * Asked on the first step only, while the grants are required on every step. So a grant macOS revokes
 * later is not recovered in this session: recording and the assistant stay off, and the hire finishes
 * on the manual steps. Accepted rather than overlooked -- that is the behaviour before any of this
 * existed, and the alternative is a takeover replacing the wizard mid-flow.
 *
 * @param isJourneyCaptureAvailable The rollout answer, and not optional: a consent record outlives the
 * flag being turned off, so without it a hire who consented yesterday would still be held here today.
 * Deliberately narrower than HomeContent's `mayOfferCapture`, which also requires a settled hiring
 * region -- nothing here is region-derived, and waiting on one would withhold the walkthrough for as
 * long as a returning hire's manual identity submission stays pending.
 */
export const shouldAskMacOsPermissions = (
  isJourneyCaptureAvailable: boolean,
  consent: JourneyConsentState,
  currentStepNum: number,
  isAlreadyAnswered: boolean
): boolean =>
  isJourneyCaptureAvailable &&
  getOS() === OPERATING_SYSTEM.MAC &&
  consent.kind === "granted" &&
  isStepThatAsks(currentStepNum) &&
  !isAlreadyAnswered &&
  isPermissionFlowAvailable();
